import React, { useEffect, useState } from "react";

import Button from "@/components/settings/Button";
import Callout from "@/components/settings/Callout";
import { useSaveStatusReporter } from "@/components/settings/SaveStatus";
import { ChoiceGroup } from "@/components/settings/SettingRow";
import { notify } from "@/components/shared/AppToast";
import { updateRoom } from "@/components/http/events";
import { t, tn } from "@/localization";
import useRoomGroup from "./useRoomGroup";
import { isCameraGroup } from "./multicam";
import {
  forNoun,
  isRoom,
  roomLabelOf,
  roomNounOf,
  ticketingModeOf,
} from "./rooms";

// The ticket choices' sentences for each noun, so a translation can agree
// with it ("todas las salas", "todos los escenarios").
const TICKET_WORDS = {
  Room: () => ({
    oneCovers: t("One ticket covers every room"),
    soldOn: (main, room) =>
      t(
        "Tickets are sold on {main} (the main link). Buyers can watch {room} and every other room with the same ticket. To sell each room separately, change it there.",
        { main, room }
      ),
    ticketsFor: (count) =>
      tn(
        "Tickets for your {count} room",
        "Tickets for your {count} rooms",
        count
      ),
    oneForAll: t("One ticket for all rooms"),
    watchEvery: t("Set it up below. Buyers can watch every room."),
    separate: t("Separate ticket per room"),
    eachPrice: t("Set each room's price on its own Sell Tickets page."),
  }),
  Stage: () => ({
    oneCovers: t("One ticket covers every stage"),
    soldOn: (main, room) =>
      t(
        "Tickets are sold on {main} (the main link). Buyers can watch {room} and every other stage with the same ticket. To sell each stage separately, change it there.",
        { main, room }
      ),
    ticketsFor: (count) =>
      tn(
        "Tickets for your {count} stage",
        "Tickets for your {count} stages",
        count
      ),
    oneForAll: t("One ticket for all stages"),
    watchEvery: t("Set it up below. Buyers can watch every stage."),
    separate: t("Separate ticket per stage"),
    eachPrice: t("Set each stage's price on its own Sell Tickets page."),
  }),
  Track: () => ({
    oneCovers: t("One ticket covers every track"),
    soldOn: (main, room) =>
      t(
        "Tickets are sold on {main} (the main link). Buyers can watch {room} and every other track with the same ticket. To sell each track separately, change it there.",
        { main, room }
      ),
    ticketsFor: (count) =>
      tn(
        "Tickets for your {count} track",
        "Tickets for your {count} tracks",
        count
      ),
    oneForAll: t("One ticket for all tracks"),
    watchEvery: t("Set it up below. Buyers can watch every track."),
    separate: t("Separate ticket per track"),
    eachPrice: t("Set each track's price on its own Sell Tickets page."),
  }),
  Court: () => ({
    oneCovers: t("One ticket covers every court"),
    soldOn: (main, room) =>
      t(
        "Tickets are sold on {main} (the main link). Buyers can watch {room} and every other court with the same ticket. To sell each court separately, change it there.",
        { main, room }
      ),
    ticketsFor: (count) =>
      tn(
        "Tickets for your {count} court",
        "Tickets for your {count} courts",
        count
      ),
    oneForAll: t("One ticket for all courts"),
    watchEvery: t("Set it up below. Buyers can watch every court."),
    separate: t("Separate ticket per court"),
    eachPrice: t("Set each court's price on its own Sell Tickets page."),
  }),
  Field: () => ({
    oneCovers: t("One ticket covers every field"),
    soldOn: (main, room) =>
      t(
        "Tickets are sold on {main} (the main link). Buyers can watch {room} and every other field with the same ticket. To sell each field separately, change it there.",
        { main, room }
      ),
    ticketsFor: (count) =>
      tn(
        "Tickets for your {count} field",
        "Tickets for your {count} fields",
        count
      ),
    oneForAll: t("One ticket for all fields"),
    watchEvery: t("Set it up below. Buyers can watch every field."),
    separate: t("Separate ticket per field"),
    eachPrice: t("Set each field's price on its own Sell Tickets page."),
  }),
  Hall: () => ({
    oneCovers: t("One ticket covers every hall"),
    soldOn: (main, room) =>
      t(
        "Tickets are sold on {main} (the main link). Buyers can watch {room} and every other hall with the same ticket. To sell each hall separately, change it there.",
        { main, room }
      ),
    ticketsFor: (count) =>
      tn(
        "Tickets for your {count} hall",
        "Tickets for your {count} halls",
        count
      ),
    oneForAll: t("One ticket for all halls"),
    watchEvery: t("Set it up below. Buyers can watch every hall."),
    separate: t("Separate ticket per hall"),
    eachPrice: t("Set each hall's price on its own Sell Tickets page."),
  }),
  Camera: () => ({
    oneCovers: t("One ticket covers every camera"),
    soldOn: (main, room) =>
      t(
        "Tickets are sold on {main} (the main link). Buyers can watch {room} and every other camera with the same ticket.",
        { main, room }
      ),
    ticketsFor: (count) =>
      tn(
        "Tickets for your {count} camera",
        "Tickets for your {count} cameras",
        count
      ),
    oneForAll: t("One ticket for all cameras"),
    watchEvery: t("Set it up below. Buyers can watch every camera."),
    separate: t("Separate ticket per camera"),
    eachPrice: t("Set each camera's price on its own Sell Tickets page."),
  }),
};

// Sell Tickets for rooms. On a primary with rooms: choose one ticket for all
// rooms (default) or a ticket per room. On a room that shares the primary's
// tickets: a pointer to the primary instead of its own ticket settings.
// Returns { hideTicketSettings, roomsTicketing } for SellTickets to render.
const useRoomsTicketing = (event, navigation) => {
  const { primary, rooms } = useRoomGroup(event);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState(null);
  // Saves right away; the screen header shows Saving… / Saved like auto-save.
  const report = useSaveStatusReporter("rooms-ticketing");

  // A camera group saved with a ticket per camera (possible before cameras
  // always shared one) goes back to one ticket, so evt.live sells one too.
  const healCameraTickets =
    !isRoom(event) &&
    isCameraGroup(event) &&
    event?.rooms_ticketing === "per_room";
  useEffect(() => {
    if (!healCameraTickets) return;
    updateRoom(event.slug, { rooms_ticketing: "shared" }).catch(() => {});
  }, [healCameraTickets, event?.slug]);

  if (isRoom(event)) {
    if (!primary || ticketingModeOf(primary) !== "shared") {
      return { hideTicketSettings: false, roomsTicketing: null };
    }
    const words = forNoun(TICKET_WORDS, roomNounOf(primary));
    return {
      hideTicketSettings: true,
      roomsTicketing: (
        <Callout
          tone="info"
          title={words.oneCovers}
          action={
            <Button
              text={t("Open {label} tickets", { label: roomLabelOf(primary) })}
              size="md"
              fullWidth={false}
              onPress={() =>
                navigation.navigate("SellTickets", {
                  slug: primary.slug,
                  key: primary.slug,
                })
              }
            />
          }
        >
          {words.soldOn(roomLabelOf(primary), roomLabelOf(event))}
        </Callout>
      ),
    };
  }

  if (!rooms.length) {
    return { hideTicketSettings: false, roomsTicketing: null };
  }

  const words = forNoun(TICKET_WORDS, roomNounOf(event));

  // Cameras: one ticket for the whole event, nothing to choose.
  if (isCameraGroup(event)) {
    return {
      hideTicketSettings: false,
      roomsTicketing: (
        <Callout tone="info" title={words.oneForAll}>
          {words.watchEvery}
        </Callout>
      ),
    };
  }

  const current = mode || ticketingModeOf(event);
  const choose = async (next) => {
    if (next === current || saving) return;
    setSaving(true);
    setMode(next);
    report?.({ state: "saving", at: Date.now() });
    try {
      await updateRoom(event.slug, { rooms_ticketing: next });
      if (report) report({ state: "saved", at: Date.now() });
      else notify.success(t("Saved"));
    } catch (err) {
      setMode(null);
      const message = err?.errors?.[0]?.message || err?.message;
      if (report)
        report({
          state: "error",
          message,
          retry: () => choose(next),
          at: Date.now(),
        });
      else notify.error(t("Couldn't save"), message);
    } finally {
      setSaving(false);
    }
  };

  return {
    hideTicketSettings: false,
    roomsTicketing: (
      <ChoiceGroup
        accessibilityLabel={words.ticketsFor(rooms.length + 1)}
        value={current}
        onChange={choose}
        options={[
          {
            value: "shared",
            label: words.oneForAll,
            description: words.watchEvery,
            disabled: saving,
          },
          {
            value: "per_room",
            label: words.separate,
            description: words.eachPrice,
            disabled: saving,
          },
        ]}
      />
    ),
  };
};

export default useRoomsTicketing;
